(ns manage.ad-variant.creation.manual-upload-ocp.hooks
  (:require [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.form :as form]
            [common.queries :refer [use-mutation]]
            [common.util :as util]
            [lemonpi.ads-schema.interface :as ads-schema]
            [malli.core :as m]
            [malli.error :as me]
            [manage.ad-variant.data :as d]
            [manage.ad-variant.queries :as q]
            [medley.core :as medley]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn get-ad-variant-path->form-path [format]
  (merge
   {[:ad-variant-uuid] [:ad-variant-uuid]
    [:ad-type] [:format]
    [:content-id] [:content-id]
    [:targeting-values] [:targeting-values]
    [:scheduling] [:scheduling]}
   (case format
     ("tiktok-single-video" "tiktok-single-image")
     {[:components :urn:tiktok:cta :value] [:components :cta]
      [:components :urn:tiktok:ad-name :value] [:components :ad-name]
      [:components :urn:tiktok:ad-text :value] [:components :ad-text]
      [:components :urn:tiktok:single-image :value] [:components :image :url]
      [:components :urn:tiktok:single-video :value] [:components :video :url]
      [:components :urn:tiktok:click-through :value] [:components :click-through]}

     ("meta-single-image" "meta-single-video")
     {[:components :urn:meta:cta :value] [:components :cta]
      [:components :urn:meta:cta-caption :value] [:components :cta-caption]
      [:components :urn:meta:headline :value] [:components :headline]
      [:components :urn:meta:description :value] [:components :description]
      [:components :urn:meta:primary-text :value] [:components :primary-text]
      [:components :urn:meta:single-image :value] [:components :image :url]
      [:components :urn:meta:single-video :value] [:components :video :url]
      [:components :urn:meta:click-through :value] [:components :click-through]}

     ;; vast-aws-prime is just vast-linear tagged for Amazon STV, same components
     ("vast-linear" "vast-aws-prime")
     {[:components :urn:lemonpi:vast:video-1 :value] [:components :video-1 :url]
      [:components :urn:lemonpi:vast:video-2 :value] [:components :video-2 :url]
      [:components :urn:lemonpi:vast:video-3 :value] [:components :video-3 :url]
      [:components :urn:lemonpi:vast:video-4 :value] [:components :video-4 :url]
      [:components :urn:lemonpi:vast:video-5 :value] [:components :video-5 :url]
      [:components :urn:lemonpi:vast:video-6 :value] [:components :video-6 :url]
      [:components :urn:lemonpi:vast:video-7 :value] [:components :video-7 :url]
      [:components :urn:lemonpi:vast:video-8 :value] [:components :video-8 :url]
      [:components d/amazon-stv-component-key :value] [:components :video :url]
      [:components :urn:vast:click-through :value] [:components :click-through]
      [:placements] [:placement]}

     "display-html"
     {[:components :urn:lemonpi:artefact:display:content+template :value :template :base-url] [:components :template :base-url]
      [:components :urn:lemonpi:artefact:display:content+template :value :content] [:components :content]
      [:components :urn:lemonpi:artefact:display:click-through :value] [:components :click-through]
      [:dimensions :width] [:components :template :width]
      [:dimensions :height] [:components :template :height]}

     "meta-carousel"
     {[:components :urn:meta:primary-text :value] [:components :primary-text]
      [:components :urn:meta:cta :value]          [:components :cta]
      [:components :urn:meta:carousel:cards :value] [:components :cards]}
     nil)))

(def form-path->form-convert
  {[:components :content] #(keep (fn [[k v]]
                                   (when (= "click" (:type v))
                                     {:key (name k) :value (or (:value v) "")}))
                                 %)

   ;; An ad-variant can have several placements, the upload form only ever picks one.
   [:placement] first

   ;; The form holds the media of a card as a list, the stored card holds either a
   ;; single media or one media per placement format
   [:components :cards]
   (fn [cards]
     (mapv (fn [card]
             (let [medias (ads-schema/carousel-card-media-list card)]
               (assoc card :media
                      (if (= "video" (:kind (first medias)))
                        {:kind "video"
                         :videos (mapv #(select-keys % [:url :thumbnail]) medias)}
                        {:kind "image"
                         ;; Keep the dimensions the media already carries, so the preview can
                         ;; keep matching cards to the aspect ratio they were customized for.
                         :urls (mapv #(select-keys % [:url :width :height]) medias)}))))
           cards))})

(def form-path->be-convert
  {[:components :content]
   #(->> %
         (keep (fn [{:keys [key value]}]
                 (when (seq key)
                   [(keyword key) {:type "click" :value (or value "")}])))
         (into {}))

   ;; A card with one media keeps the single media the upload has always sent, a card
   ;; with several sends one media per placement format so the carousel can be
   ;; customized per Meta placement
   [:components :cards]
   (fn [cards]
     (mapv (fn [{:keys [media] :as card}]
             (let [medias (if (= "video" (:kind media))
                            (into []
                                  (comp (remove (comp str/blank? :url))
                                        (map (fn [{:keys [url thumbnail]}]
                                               (cond-> {:kind "video" :url url}
                                                 (not (str/blank? (:url thumbnail)))
                                                 (assoc :thumbnail thumbnail)))))
                                  (:videos media))
                            (into []
                                  (comp (remove (comp str/blank? :url))
                                        (map (fn [{:keys [url width height]}]
                                               (cond-> {:kind "image" :url url}
                                                 (and width height) (assoc :width width :height height)))))
                                  (:urls media)))]
               (case (count medias)
                 0 (dissoc card :media)
                 1 (assoc card :media (first medias))
                 (assoc card :media medias))))
           cards))

   [:components :template :height] js/parseInt
   [:components :template :width] js/parseInt})

(defn ad-variant->form-ad [{:keys [ad-type] :as ad-variant}]
  (let [form-ad (reduce (fn [acc [ad-variant-path form-path]]
                          (let [path-value (get-in ad-variant ad-variant-path)
                                convert-fn (get form-path->form-convert form-path identity)]
                            (cond-> acc
                              path-value (assoc-in form-path (convert-fn path-value)))))
                        {}
                        (get-ad-variant-path->form-path ad-type))]
    (-> form-ad
        (update :ad-variant-uuid uuid)
        (assoc :schedule-enabled (seq (get-in ad-variant [:scheduling :timezone])))
        ;; The ad-variant itself doesn't remember :processing, only the placement a
        ;; transcode produced it for, but only a transcode has one at all.
        (cond-> (:placement form-ad) (assoc :processing "transcode"))
        ->js)))

(defn create-default-ad-value [& [format]]
  (js/Object.assign #js {:fe-id (random-uuid)
                         :targeting-values #js []}
                    (when format #js {:format format})))

(defn ->initial-value [initial-ad]
  (ad-variant->form-ad initial-ad))

(def error-messages
  {:format "Please choose an format"
   :content-id "Please provide a name"
   :components {:image {:url "Please add an image"}
                :video {:url "Please add a video"}
                :video-1 {:url "Please add a video"}
                :video-2 {:url "Please add a video"}
                :video-3 {:url "Please add a video"}
                :video-4 {:url "Please add a video"}
                :video-5 {:url "Please add a video"}
                :video-6 {:url "Please add a video"}
                :video-7 {:url "Please add a video"}
                :video-8 {:url "Please add a video"}
                :primary-text "Please provide a primary text"
                :click-through "Please provide a valid URL"
                :headline "Please provide a headline"
                :cta "Please choose a valid call to action"
                :template {:width "Please provide a width"
                           :height "Please provide a height"
                           :base-url "Please upload a template"}}
   :targeting-values {:name "Please provide a key"}})

(defn- humanize
  "Wraps `malli.error/humanize` and replaces the default error messages
   with frontend-friendly messages when available, falls back to default malli message."
  [explanation]
  (letfn [(wrap [{:keys [message in]}]
            (or (get-in error-messages (remove int? in))
                (when (not= message "missing required key") message)
                ""))]
    (me/humanize explanation {:wrap wrap})))

(def ^:private explainer (m/explainer ads-schema/Ad))

(defn- coerce-ad [ad]
  (-> (reduce (fn [acc [form-path be-convert]]
                (medley/update-existing-in acc form-path be-convert))
              ad
              form-path->be-convert)
      ;; Switching video format (OLV <-> STV) nils out the other format's url
      ;; fields, which would otherwise leave behind empty component maps like
      ;; {:video-2 {}}.
      (util/filter-nils-map :remove-blank-strings? true :remove-empty-maps? true)))

(defn carousel-card-media-count
  "How many media a card carries in the form, empty upload slots don't count."
  [{:keys [media] :as _card}]
  (if (= "video" (:kind media))
    (count (remove (comp str/blank? :url) (:videos media)))
    (count (remove (comp str/blank? :url) (:urls media)))))

(defn- carousel-media-errors
  "Card media errors the ad schema cannot express: a card needs at least one media,
   and every card needs as many media as the others, because a carousel that is
   customized per Meta placement serves one whole card set per placement. Returns a map
   of card index to error, keyed like the humanized schema errors. Video card errors
   anchor to the first video slot, which is always rendered."
  [{:keys [format] :as form-ad}]
  (when (= "meta-carousel" format)
    (let [cards (get-in form-ad [:components :cards])
          video? (fn [card] (= "video" (get-in card [:media :kind])))
          counts (mapv carousel-card-media-count cards)
          reference (first (filter pos? counts))
          media-error (fn [card messages]
                        (if (video? card)
                          {:media {:videos {0 {:url messages}}}}
                          {:media {:urls messages}}))
          mismatch-msg (str "All cards must have the same number of media, the first card with media has " reference)]
      (into {}
            (keep (fn [[idx card]]
                    (let [n (nth counts idx)]
                      (cond
                        (zero? n)
                        [idx (media-error card [(if (video? card) "Please add a video" "Please add an image")])]

                        (not= n reference)
                        [idx (media-error card [mismatch-msg])]

                        :else nil))))
            (map-indexed vector cards)))))

(defn- merge-card-errors
  "The humanized schema errors hold the cards as a sparse vector, the custom media
   errors as an index map. Indexes both and merges, the custom media error replacing
   the schema one so a card shows a single message."
  [schema-card-errors card-errors]
  (let [indexed (cond
                  (map? schema-card-errors) schema-card-errors
                  (some? schema-card-errors) (into {}
                                                   (keep-indexed (fn [idx err] (when err [idx err])))
                                                   schema-card-errors)
                  :else {})]
    (reduce-kv (fn [acc idx err] (update acc idx merge err))
               indexed
               card-errors)))

(defn resolver [data]
  (let [form-ad (->clj data)
        errors (->> form-ad coerce-ad explainer humanize)
        card-errors (carousel-media-errors form-ad)
        errors (cond-> errors
                 (seq card-errors)
                 (assoc-in [:components :cards]
                           (merge-card-errors (get-in errors [:components :cards]) card-errors)))]
    (form/arrange-malli-errors data errors)))

(defhook use-ad-variant-upload-form [{:keys [initial-ad single-format add-more on-reset]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        values (uix/use-memo #(or (some-> initial-ad ->initial-value)
                                  (create-default-ad-value single-format))
                             [initial-ad single-format])
        {:keys [handleSubmit reset] :as form-control}
        (form/use-form #js {:mode "onBlur"
                            :reValidateMode "onBlur"
                            :values values
                            :resolver resolver})

        {upload-ads :mutate} (use-mutation (q/upload-ads-mutation
                                            {:advertiser-id advertiser-id
                                             :adset-id adset-id
                                             :success-message (if initial-ad
                                                                "Successfully updated ad"
                                                                "Successfully created ad")}))

        [submitting set-submitting] (uix/use-state false)
        on-submit (uix/use-callback
                   (fn [form-data]
                     (let [clj-values (->clj form-data)
                           ad (-> clj-values
                                  (dissoc :fe-id :schedule-enabled)
                                  (medley/update-existing :ad-variant-uuid str)
                                  (cond-> (not (:schedule-enabled clj-values)) (dissoc :scheduling))
                                  coerce-ad)
                           payload {:ads [ad]}]
                       (set-submitting true)
                       (upload-ads payload
                                   #js {:onSuccess (fn []
                                                     (set-submitting false)
                                                     (if add-more
                                                       (do (reset values)
                                                           (when on-reset (on-reset)))
                                                       (rf/dispatch [:modal/close])))
                                        :onError #(set-submitting false)})))
                   [upload-ads add-more on-reset reset values])]
    {:form-control form-control
     :on-submit (handleSubmit on-submit)
     :submitting submitting}))

(defn form-values>preview-ad-variant [{:keys [format] :as form-values}]
  (let [coerced-ad (coerce-ad form-values)]
    (reduce
     (fn [ad-variant [ad-variant-path form-path]]
       (let [value (get-in coerced-ad form-path)]
         (cond-> ad-variant
           value (assoc-in ad-variant-path value))))
     {}
     (get-ad-variant-path->form-path format))))

(defn ad-type->schema
  [multi-schema ad-type]
  (let [[_ _ & cases] multi-schema]
    (some (fn [[k schema]]
            (when (= k ad-type)
              schema))
          cases)))

(defn- unwrap-and
  "Ad schemas are wrapped as [:and <shape> <constraint>] to carry constraints the
   shape itself can't express. The shape is the first branch."
  [schema]
  (let [schema (m/schema schema)]
    (if (= :and (m/type schema))
      (recur (first (m/children schema)))
      schema)))

(defn multi-schema-branch
  "vast-linear's own schema is a :multi on :processing, since a transcoded
   upload has different components than a direct one. Picks the matching
   branch, or returns other ad-types' (plain map) schemas as-is."
  [schema processing]
  (let [schema (unwrap-and schema)]
    (if (= :multi (m/type schema))
      (some (fn [[k _ s]] (when (= k processing) s)) (m/children schema))
      schema)))

(defn schema->components-schema
  [schema]
  (some (fn [[k _ v]] (when (= :components k) v))
        (m/children (unwrap-and schema))))

(defn components-schema->component-keys
  [component-schema]
  (map first (m/children component-schema)))

(defn component-keys-for-ad-type
  [multi-schema ad-type processing]
  (some-> multi-schema
          (ad-type->schema ad-type)
          (multi-schema-branch (or processing "direct"))
          schema->components-schema
          components-schema->component-keys))

(defhook use-component-keys [ad-type processing]
  (component-keys-for-ad-type ads-schema/Ad ad-type processing))

(defhook use-supported-ad-types [adset]
  (let [[_ _ & cases] ads-schema/Ad
        display-manual-ad-upload? (use-flag :display-manual-ad-upload)
        meta-carousel-manual-upload? (use-flag :meta-carousel-manual-upload)
        manual-upload-supported-ad-types (map first cases)]
    (cond-> (select-keys (d/adset->ad-types adset) manual-upload-supported-ad-types)
      (not display-manual-ad-upload?) (dissoc "display-html")
      (not meta-carousel-manual-upload?) (dissoc "meta-carousel"))))

(defhook use-manual-upload-enabled? [adset]
  (let [ad-variant-manual-upload-enabled? (use-flag :ad-variant-manual-upload)
        supported-ad-types (use-supported-ad-types adset)]
    (and ad-variant-manual-upload-enabled?
         (boolean (seq supported-ad-types)))))

(defhook use-ad-type-options [adset]
  (->> (use-supported-ad-types adset)
       (map (fn [[ad-type {:keys [title]}]]
              {:value ad-type
               :label title}))))

(defhook use-cta-options [ad-type]
  (let [options (case ad-type
                  ("meta-single-image" "meta-single-video" "meta-carousel") ads-schema/meta-cta-options
                  ("tiktok-single-image" "tiktok-single-video") ads-schema/tiktok-cta-options
                  [])]
    (->> options
         (mapv (fn [option]
                 {:value (:key option)
                  :label (:label option)})))))

(defhook use-template-upload []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        [overlay-state set-overlay-state!] (uix/use-state nil)
        {mutate :mutate} (use-mutation (q/upload-template-mutation
                                        {:advertiser-id advertiser-id}))]
    {:upload-template (fn [params ^js callbacks]
                        (set-overlay-state! {:state :pending})
                        (mutate params
                                #js {:onSuccess (fn [resp]
                                                  (set-overlay-state! {:state :success})
                                                  (when-let [cb (.-onSuccess callbacks)]
                                                    (cb resp)))
                                     :onError (fn [err]
                                                (set-overlay-state! {:state :failure})
                                                (when-let [cb (.-onError callbacks)]
                                                  (cb err)))}))
     :overlay-state overlay-state}))
